import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { Product, Category } from '../../types';
import { addProduct, updateProduct, deleteProduct, getProducts } from '../../firebase/products';
import { getCategories } from '../../firebase/categories';
import { uploadProductImage } from '../../firebase/storage';
import RichTextEditor from '../RichTextEditor';
import TimePriceManager from '../TimePriceManager';
import toast from 'react-hot-toast';

const ProductManager = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    shortDescription: '',
    description: '',
    price: '',
    categoryId: '',
    subcategoryId: '',
    images: [] as { url: string; isPrimary: boolean }[],
    timePrices: [] as { id: string; time: string; price: number }[],
    featuredPosition: null as number | null,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      setIsLoading(true);
      const [productsData, categoriesData] = await Promise.all([
        getProducts(),
        getCategories()
      ]);
      setProducts(productsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Error al cargar los datos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setNewProduct({
        name: product.name,
        shortDescription: product.shortDescription || '',
        description: product.description,
        price: product.price.toString(),
        categoryId: product.categoryId,
        subcategoryId: product.subcategoryId || '',
        images: product.images,
        timePrices: product.timePrices || [],
        featuredPosition: product.featuredPosition || null,
      });
    } else {
      setEditingProduct(null);
      setNewProduct({
        name: '',
        shortDescription: '',
        description: '',
        price: '',
        categoryId: '',
        subcategoryId: '',
        images: [],
        timePrices: [],
        featuredPosition: null,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProduct(null);
    setNewProduct({
      name: '',
      shortDescription: '',
      description: '',
      price: '',
      categoryId: '',
      subcategoryId: '',
      images: [],
      timePrices: [],
      featuredPosition: null,
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const imageUrl = await uploadProductImage(file);
      setNewProduct({
        ...newProduct,
        images: [
          ...newProduct.images,
          { url: imageUrl, isPrimary: newProduct.images.length === 0 },
        ],
      });
      toast.success('Imagen subida correctamente');
    } catch (error) {
      console.error('Error al subir la imagen:', error);
      toast.error('Error al subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index: number) => {
    const newImages = newProduct.images.filter((_, i) => i !== index);
    if (newProduct.images[index].isPrimary && newImages.length > 0) {
      newImages[0].isPrimary = true;
    }
    setNewProduct({ ...newProduct, images: newImages });
  };

  const handleSetPrimaryImage = (index: number) => {
    const updatedImages = newProduct.images.map((img, i) => ({
      ...img,
      isPrimary: i === index,
    }));
    setNewProduct({ ...newProduct, images: updatedImages });
  };

  const isPositionTaken = (position: number, currentProductId?: string) => {
    return products.some(
      p => p.featuredPosition === position && p.id !== currentProductId
    );
  };

  const handleSubmit = async () => {
    try {
      setError(null);

      if (!newProduct.name.trim()) {
        setError('El nombre del producto es obligatorio');
        return;
      }

      if (!newProduct.categoryId) {
        setError('La categoría es obligatoria');
        return;
      }

      if (!newProduct.price || parseFloat(newProduct.price) <= 0) {
        setError('El precio debe ser mayor que 0');
        return;
      }

      if (newProduct.images.length === 0) {
        setError('Debe agregar al menos una imagen');
        return;
      }

      if (newProduct.featuredPosition !== null) {
        if (isPositionTaken(newProduct.featuredPosition, editingProduct?.id)) {
          setError('Esta posición destacada ya está ocupada');
          return;
        }
      }

      if (newProduct.timePrices.length > 0) {
        const invalidTimePrices = newProduct.timePrices.some(tp => !tp.time || tp.price <= 0);
        if (invalidTimePrices) {
          setError('Todos los precios por tiempo deben tener un tiempo válido y un precio mayor que 0');
          return;
        }
      }

      const productData = {
        name: newProduct.name.trim(),
        shortDescription: newProduct.shortDescription.trim(),
        description: newProduct.description.trim(),
        price: parseFloat(newProduct.price),
        categoryId: newProduct.categoryId,
        subcategoryId: newProduct.subcategoryId || null,
        images: newProduct.images,
        timePrices: newProduct.timePrices,
        featuredPosition: newProduct.featuredPosition,
      };

      if (editingProduct) {
        await updateProduct(editingProduct.id, productData);
        toast.success('Producto actualizado correctamente');
      } else {
        await addProduct(productData);
        toast.success('Producto creado correctamente');
      }
      
      await loadData();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving product:', error);
      setError('Error al guardar el producto. Por favor, inténtalo de nuevo.');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteProduct(id);
      await loadData();
      toast.success('Producto eliminado correctamente');
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Error al eliminar el producto');
    }
  };

  const mainCategories = categories.filter(cat => !cat.parentId);
  const subCategories = categories.filter(cat => cat.parentId);

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Gestionar Productos
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Producto
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Imagen</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Categoría</TableCell>
              <TableCell>Subcategoría</TableCell>
              <TableCell>Precio Base</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product) => {
              const category = categories.find(c => c.id === product.categoryId);
              const subcategory = categories.find(c => c.id === product.subcategoryId);
              const primaryImage = product.images.find(img => img.isPrimary);

              return (
                <TableRow key={product.id}>
                  <TableCell sx={{ width: 100 }}>
                    {primaryImage && (
                      <img
                        src={primaryImage.url}
                        alt={product.name}
                        style={{
                          width: '80px',
                          height: '80px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{category?.name || 'Sin categoría'}</TableCell>
                  <TableCell>{subcategory?.name || '-'}</TableCell>
                  <TableCell>{product.price}€</TableCell>
                  <TableCell>{product.createdAt.toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(product)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(product.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <TextField
              fullWidth
              label="Nombre"
              value={newProduct.name}
              onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
            />

            <FormControl fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select
                value={newProduct.categoryId}
                label="Categoría"
                onChange={(e) => setNewProduct({
                  ...newProduct,
                  categoryId: e.target.value,
                  subcategoryId: '',
                })}
              >
                <MenuItem value="">Sin categoría</MenuItem>
                {mainCategories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {newProduct.categoryId && (
              <FormControl fullWidth>
                <InputLabel>Subcategoría (opcional)</InputLabel>
                <Select
                  value={newProduct.subcategoryId}
                  label="Subcategoría (opcional)"
                  onChange={(e) => setNewProduct({
                    ...newProduct,
                    subcategoryId: e.target.value,
                  })}
                >
                  <MenuItem value="">Sin subcategoría</MenuItem>
                  {subCategories
                    .filter(sub => sub.parentId === newProduct.categoryId)
                    .map((subcat) => (
                      <MenuItem key={subcat.id} value={subcat.id}>
                        {subcat.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              label="Precio Base"
              type="number"
              value={newProduct.price}
              onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
              InputProps={{
                startAdornment: '€',
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Posición Destacada</InputLabel>
              <Select
                value={newProduct.featuredPosition === null ? '' : newProduct.featuredPosition}
                label="Posición Destacada"
                onChange={(e) => setNewProduct({
                  ...newProduct,
                  featuredPosition: e.target.value === '' ? null : Number(e.target.value)
                })}
              >
                <MenuItem value="">No destacado</MenuItem>
                {[1, 2, 3, 4, 5, 6].map((position) => (
                  <MenuItem
                    key={position}
                    value={position}
                    disabled={isPositionTaken(position, editingProduct?.id)}
                  >
                    Posición {position}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle1" gutterBottom>
              Descripción Corta
            </Typography>
            <RichTextEditor
              content={newProduct.shortDescription}
              onChange={(content) => setNewProduct({ ...newProduct, shortDescription: content })}
            />

            <Typography variant="subtitle1" gutterBottom>
              Descripción Detallada
            </Typography>
            <RichTextEditor
              content={newProduct.description}
              onChange={(content) => setNewProduct({ ...newProduct, description: content })}
            />

            <Divider />

            <TimePriceManager
              timePrices={newProduct.timePrices}
              onChange={(timePrices) => setNewProduct({ ...newProduct, timePrices })}
            />

            <Divider />

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Imágenes
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={uploading ? <CircularProgress size={20} /> : <ImageIcon />}
                  disabled={uploading}
                >
                  Subir Imagen
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {newProduct.images.map((image, index) => (
                  <Paper
                    key={index}
                    sx={{
                      position: 'relative',
                      width: 200,
                      p: 1,
                      border: image.isPrimary ? '2px solid #4F46E5' : undefined,
                    }}
                  >
                    <img
                      src={image.url}
                      alt={`Producto ${index + 1}`}
                      style={{
                        width: '100%',
                        height: 150,
                        objectFit: 'cover',
                        borderRadius: 4,
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Button
                        size="small"
                        onClick={() => handleSetPrimaryImage(index)}
                        disabled={image.isPrimary}
                      >
                        Principal
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveImage(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingProduct ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ProductManager;